import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { patch } from '@ngxs/store/operators';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ICompetenceMatrix } from './competence-matrix.model';
import { CompetenceMatrixApiService } from './competence-matrix-api.service';
import {
  LoadCompetenceMatrix,
  PrintCompetenceMatrix,
  SendCompetenceMatrixNotifications,
} from './competence-matrix.actions';

export interface CompetenceMatrixStateModel {
  matrix: ICompetenceMatrix | undefined;
  matrixFile: Blob | undefined;
}

const COMPETENCE_MATRIX_STATE_TOKEN = new StateToken<CompetenceMatrixStateModel>(
  'competenceMatrixState'
);

@State<CompetenceMatrixStateModel>({
  name: COMPETENCE_MATRIX_STATE_TOKEN,
  defaults: {
    matrix: undefined,
    matrixFile: undefined,
  },
})
@Injectable()
export class CompetenceMatrixState {
  constructor(private service: CompetenceMatrixApiService) {}

  @Action(LoadCompetenceMatrix, { cancelUncompleted: true })
  loadMatrix(
    { setState }: StateContext<CompetenceMatrixStateModel>,
    { params, adminPanel }: LoadCompetenceMatrix
  ) {
    return this.service.getMatrix(params, adminPanel).pipe(
      tap((matrix: ICompetenceMatrix) => {
        setState(patch({ matrix }));
      })
    );
  }

  @Action(PrintCompetenceMatrix, { cancelUncompleted: true })
  printMatrix(
    { setState }: StateContext<CompetenceMatrixStateModel>,
    { config }: PrintCompetenceMatrix
  ) {
    if (config.fileType === 'excel') {
      return this.service.printMatrixAsExcel(config).pipe(
        tap((matrixFile: Blob) => {
          setState(patch({ matrixFile }));
        })
      );
    }
    return this.service.printMatrixAsPdf(config).pipe(
      tap((matrixFile: Blob) => {
        setState(patch({ matrixFile }));
      })
    );
  }

  @Action(SendCompetenceMatrixNotifications, { cancelUncompleted: true })
  @OnSuccess({ message: 'competenceMatrixNotificationsSentSuccessfully' })
  sendNotifications(
    { setState }: StateContext<CompetenceMatrixStateModel>,
    { config }: SendCompetenceMatrixNotifications
  ) {
    return this.service.sendMatrixNotifications(config);
  }

  @Selector()
  static matrix(state: CompetenceMatrixStateModel) {
    return state.matrix;
  }

  @Selector()
  static matrixFile(state: CompetenceMatrixStateModel) {
    return state.matrixFile;
  }
}
